import { useCallback } from 'react';

import { useLanguageStore } from '../../features/quiz-core/languageStore';
import { t, translations } from '../../i18n/translations';

type Vars = Record<string, string | number>;

const fill = (text: string, vars?: Vars) =>
  vars ? text.replace(/\{\{(\w+)\}\}/g, (_match, name: string) => String(vars[name] ?? '')) : text;

/**
 * Localized UI copy from the `uiCopy` module. A key with a dot ('familiesFriends.x') is
 * looked up as-is. Reads the current language at call time, so it is safe in alert callbacks.
 */
export const uiText = (key: string, vars?: Vars, language?: string): string => {
  const lang = language ?? useLanguageStore.getState().language;
  const full = key.includes('.') ? key : `uiCopy.${key}`;
  const value = t(full, lang);
  return fill(value === full ? key : value, vars);
};

/** Same as uiText but subscribes the component to language changes. */
export const useUiText = () => {
  const language = useLanguageStore((state) => state.language);
  return useCallback((key: string, vars?: Vars) => uiText(key, vars, language), [language]);
};

/**
 * Localized text keyed by its English source (premiumGateCopy). Falls back to the English
 * text, so an unlisted string still renders. English keys contain dots, so this does not go
 * through t().
 */
export const gateText = (english: string, language?: string): string => {
  const lang = language ?? useLanguageStore.getState().language;
  const table = translations[lang]?.premiumGateCopy as Record<string, string> | undefined;
  return table?.[english] ?? english;
};
